import { useCallback, useEffect, useRef, useState } from 'react';
import { SavingsField, SavingsProduct, SAVINGS_PRODUCTS_ORDER } from './RetirementCalculator.const';
import { SavingsProductValueModel, SavingsValuesModel } from './RetirementCalculator.model';

export const createEmptySavingsValues = (): SavingsValuesModel =>
  SAVINGS_PRODUCTS_ORDER.reduce((values, product) => {
    values[product] = { enabled: false };
    return values;
  }, {} as SavingsValuesModel);

interface UseSavingsFormParams {
  initialValues?: SavingsValuesModel;
  onSubmit?: (values: SavingsValuesModel) => void;
  onValuesChange?: (values: SavingsValuesModel) => void;
}

export const useSavingsForm = ({
  initialValues,
  onSubmit,
  onValuesChange,
}: UseSavingsFormParams) => {
  const [values, setValues] = useState<SavingsValuesModel>(
    () => initialValues ?? createEmptySavingsValues(),
  );

  const onValuesChangeRef = useRef(onValuesChange);
  onValuesChangeRef.current = onValuesChange;

  useEffect(() => {
    onValuesChangeRef.current?.(values);
  }, [values]);

  const setProductValue = useCallback(
    (product: SavingsProduct, patch: Partial<SavingsProductValueModel>) => {
      setValues(previousValues => ({
        ...previousValues,
        [product]: { ...previousValues[product], ...patch },
      }));
    },
    [],
  );

  const toggleProduct = useCallback(
    (product: SavingsProduct, enabled: boolean) => setProductValue(product, { enabled }),
    [setProductValue],
  );

  const setAmount = useCallback(
    (product: SavingsProduct, field: SavingsField, amount?: number) =>
      setProductValue(product, { [field]: amount }),
    [setProductValue],
  );

  const submit = useCallback(() => {
    const submitted = SAVINGS_PRODUCTS_ORDER.reduce((result, product) => {
      const { enabled, ...amounts } = values[product];
      result[product] = enabled ? { enabled, ...amounts } : { enabled };
      return result;
    }, {} as SavingsValuesModel);

    onSubmit?.(submitted);
  }, [onSubmit, values]);

  return { values, toggleProduct, setAmount, submit };
};
